import { Ban, CheckCircle2, PackageCheck, Settings, UserRound } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { usePermission } from '../../../../auth/AuthContext';
import { Breadcrumb, type BreadcrumbSegment } from '../../../../components/Breadcrumb';
import { CountBadge } from '../../../../components/CountBadge';
import { ErrorState } from '../../../../components/ErrorState';
import { Menu, type MenuItem } from '../../../../components/Menu';
import { PreviewTable } from '../../../../components/PreviewTable';
import { Skeleton } from '../../../../components/Skeleton';
import { ProfileBanner } from '../../../../components/profile/ProfileBanner';
import { ProfileCard } from '../../../../components/profile/ProfileCard';
import { ProfileHeader } from '../../../../components/profile/ProfileHeader';
import { ProfileHeaderShell } from '../../../../components/profile/ProfileHeaderShell';
import { StatusText } from '../../../../components/table/StatusText';
import { useToast } from '../../../../components/toastContext';
import { ApiError } from '../../../../lib/apiError';
import { formatDateTime } from '../../../../lib/formatDate';
import { personDisplayName, usePersonNames } from '../../../human-resources/personnel/usePersonNames';
import { IssuedAssetsPanel } from '../components/IssuedAssetsPanel';
import { RecordReturnModal } from '../components/RecordReturnModal';
import { employeeAssetsApi } from '../employeeAssetApi';
import type { EmployeeAssetDetail } from '../employeeAssetTypes';
import { issuancesApi } from '../issuanceApi';
import type { IssuanceDetail, IssuedAssetLine } from '../issuanceTypes';

type TabKey = 'info' | 'assets';

/**
 * One employee's Employee Assets profile — the history of every article issued to them.
 *
 * **It LANDS on Issued Assets**, not Info: a reader opens this to read the history, which is the
 * landing exception frontend-profiles.md sets for a record opened to be read. `Info` is still
 * the first tab declared.
 *
 * **A view over two modules' records.** The employee is Human Resources' — linked out to, never
 * edited here — and the lines are the issuance register's, issued and returned under that
 * register's own keys. So no card carries an Edit: the employee card is another module's record
 * and the figures card is derived from the lines beneath it.
 *
 * **Above the reader's personnel access level the endpoint answers 403**, and the page states
 * that refusal rather than an empty history.
 */
export function EmployeeAssetProfilePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const canReadPersonnel = usePermission('personnel.read');

  const [record, setRecord] = useState<EmployeeAssetDetail | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [tab, setTab] = useState<TabKey>('assets');

  /** The slip a return is being recorded against, fetched whole — the modal needs its lines. */
  const [returning, setReturning] = useState<{ issuance: IssuanceDetail; lineId: string } | null>(null);

  // A name needs `personnel.read`; without it the title is the employee number.
  const names = usePersonNames(canReadPersonnel && record ? [record.personId] : []);

  // A different employee is a different page — drop what is on screen first.
  useEffect(() => {
    setRecord(null);
    setError(null);
  }, [id]);

  useEffect(() => {
    if (!id) return;

    const controller = new AbortController();

    employeeAssetsApi
      .get(id, controller.signal)
      .then((next) => {
        if (controller.signal.aborted) return;
        setRecord(next);
        setError(null);
      })
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;
        setError(
          caught instanceof ApiError ? caught : new ApiError('Could not load this employee.', 0),
        );
      });

    return () => controller.abort();
  }, [id, reloadKey]);

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  async function openReturn(row: IssuedAssetLine) {
    try {
      const issuance = await issuancesApi.get(row.issuanceId);
      setReturning({ issuance, lineId: row.id });
    } catch (caught) {
      toast.failure(caught instanceof ApiError ? caught.message : 'Could not open that issuance.');
    }
  }

  const title = record
    ? canReadPersonnel
      ? personDisplayName(record, names)
      : record.employeeNumber
    : 'Employee';

  const breadcrumb: BreadcrumbSegment[] = [
    'Logistics',
    'Inventory',
    { label: 'Employee Assets', to: '/inventory/employee-assets' },
    title,
  ];

  // A failed REFRESH keeps the page; only a failed first load replaces it.
  if (error && !record) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <ErrorState message={error.message} correlationId={error.correlationId} onRetry={reload} />
      </div>
    );
  }

  if (!record) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <div className="flex flex-col gap-3 p-4">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  const actions: MenuItem[] = [
    {
      key: 'open-personnel',
      label: 'Open personnel record',
      icon: <UserRound className="h-4 w-4" aria-hidden="true" />,
      permission: 'personnel.read',
      onClick: () => navigate(`/personnel/${record.id}`),
    },
  ];

  const banner = !record.isActive
    ? {
        tone: 'red' as const,
        icon: <Ban className="h-4 w-4" aria-hidden="true" />,
        message:
          'This personnel record is deleted. Its history stays listed, and nothing new can be issued to it until it is restored under Human Resources.',
      }
    : record.outstandingQuantity > 0
      ? {
          tone: 'slate' as const,
          icon: <PackageCheck className="h-4 w-4" aria-hidden="true" />,
          message: `${record.outstandingQuantity} outstanding on custody slips. Record a return against the line when property comes back.`,
        }
      : record.issuedCount > 0
        ? {
            tone: 'slate' as const,
            icon: <CheckCircle2 className="h-4 w-4" aria-hidden="true" />,
            message: 'Nothing is outstanding — everything issued on a custody slip has come back.',
          }
        : {
            tone: 'slate' as const,
            icon: <CheckCircle2 className="h-4 w-4" aria-hidden="true" />,
            message: 'Nothing has been issued to this employee.',
          };

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col bg-slate-100">
      <Breadcrumb segments={breadcrumb} />

      <ProfileHeaderShell
        id="employee-asset-profile"
        header={
          <ProfileHeader
            id="employee-asset-profile-header"
            icon={<UserRound className="h-6 w-6" aria-hidden="true" />}
            title={title}
            meta={[
              // Never the same value twice: the number leads only when the title is the name.
              title === record.employeeNumber ? null : record.employeeNumber,
              `${record.issuedCount} issued ${record.issuedCount === 1 ? 'asset' : 'assets'}`,
              `${record.outstandingQuantity} outstanding`,
            ]}
            status={
              <StatusText tone={record.isActive ? 'active' : 'inactive'}>
                {record.isActive ? 'Active' : 'Deleted'}
              </StatusText>
            }
            createdBy={record.createdByName}
            createdAt={record.createdAt}
            actions={
              <Menu
                id="employee-asset-profile-settings"
                items={actions}
                trigger={({ open, toggle }) => (
                  <button
                    type="button"
                    onClick={toggle}
                    aria-haspopup="menu"
                    aria-expanded={open}
                    aria-label="Employee settings"
                    className="rounded-lg p-1 text-slate-500 transition hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-200"
                  >
                    <Settings className="h-4 w-4" aria-hidden="true" />
                  </button>
                )}
              />
            }
          />
        }
        banner={
          <ProfileBanner
            id="employee-asset-profile-banner"
            icon={banner.icon}
            message={banner.message}
          />
        }
        tabs={[
          { key: 'info', label: 'Info' },
          {
            key: 'assets',
            label: (
              <>
                Issued Assets <CountBadge count={record.issuedCount} />
              </>
            ),
          },
        ]}
        activeTab={tab}
        onTabChange={(next) => setTab(next as TabKey)}
        panel={tab === 'assets' ? 'bleed' : 'cards'}
      >
        {tab === 'info' ? (
          <>
            {/*
              Another module's record, so no Edit — it links out instead, under the key the
              personnel profile itself needs.
            */}
            <ProfileCard id="employee-asset-profile-employee" title="Employee" menuItems={actions}>
              <PreviewTable
                variant="table-display"
                rows={[
                  { label: 'Employee no.', value: record.employeeNumber, mono: true },
                  // A wider key buys the field; without it the row is not drawn at all.
                  ...(canReadPersonnel
                    ? [{ label: 'Name', value: personDisplayName(record, names) }]
                    : []),
                  { label: 'Personnel record', value: record.isActive ? 'Active' : 'Deleted' },
                ]}
              />
            </ProfileCard>

            {/* Derived from the lines on the next tab, so it has no Edit of its own. */}
            <ProfileCard id="employee-asset-profile-figures" title="Issued assets">
              <PreviewTable
                variant="table-display"
                rows={[
                  { label: 'Issued assets', value: record.issuedCount, mono: true },
                  { label: 'Outstanding', value: record.outstandingQuantity, mono: true },
                  {
                    label: 'Last issued',
                    value: record.lastIssuedAt == null ? null : formatDateTime(record.lastIssuedAt),
                    mono: true,
                  },
                ]}
              />
            </ProfileCard>
          </>
        ) : (
          <IssuedAssetsPanel
            id="employee-assets-issued-assets"
            fill
            rows={record.lines}
            failed={null}
            employeeIsActive={record.isActive}
            /*
              NO Issue asset here — product call, 08 Oct 2026: this profile is a history to read.
              Issuing stays on the Issuances register and the personnel profile's panel.
            */
            onReturn={(row) => void openReturn(row)}
          />
        )}
      </ProfileHeaderShell>

      {/*
        The issuance register's own return form, reused rather than re-drawn. Mounted only while
        open, because it seeds its state on mount. A save reloads the whole record: the header,
        the banner and the tab badge all read the line it changed.
      */}
      {returning ? (
        <RecordReturnModal
          open
          issuance={returning.issuance}
          initialLineId={returning.lineId}
          onClose={() => setReturning(null)}
          onSaved={() => {
            setReturning(null);
            reload();
            toast.success('Return recorded.');
          }}
        />
      ) : null}
    </div>
  );
}
